import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useTonWallet } from '../hooks/useTonWallet';
import { useAutomaton } from '../state/AutomatonContext';
import { TerminalOutput } from '../components/TerminalOutput';

const BOT_TOKEN_REGEX = /^\d+:[A-Za-z0-9_-]{35}$/;
// Conway Treasury (placeholder — replace before mainnet).
const TREASURY_ADDRESS = 'EQAUTOMATONTREASURYWAITINGTOBEDEPLOYEDXXXXXXXXXX';

const BOOT_STEPS = [
  'generating wallet...',
  'provisioning identity...',
  'funding child...',
  'executing genesis prompt...',
];

function snakeCase(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 24);
}

function MiniQuadrantPreview() {
  // Just a static suggestion: highlight lowest-density quadrant (top-left for demo).
  return (
    <div className="hairline p-3 bg-panel inline-block">
      <div className="text-2xs text-text-mono-dim mb-2 uppercase tracking-tightest">Suggested placement</div>
      <div className="grid grid-cols-2 gap-1">
        {[0, 1, 2, 3].map((q) => (
          <div
            key={q}
            className="w-16 h-16 hairline"
            style={{ background: q === 0 ? 'rgba(30,111,255,0.25)' : '#0A0A0A' }}
          />
        ))}
      </div>
      <div className="mt-2 text-2xs text-text-secondary font-mono">lowest-density quadrant</div>
    </div>
  );
}

export function Spawn() {
  const nav = useNavigate();
  const { isConnected, address, connect, spawn } = useTonWallet();
  const { reset } = useAutomaton();

  const [name, setName] = useState('');
  const [prompt, setPrompt] = useState('');
  const [token, setToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [funding, setFunding] = useState('5');
  const [creator, setCreator] = useState('');
  const [deploying, setDeploying] = useState(false);
  const [steps, setSteps] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const tokenOk = BOT_TOKEN_REGEX.test(token);
  const nameOk = name.length > 1;
  const promptOk = prompt.trim().length > 10;
  const fundingNum = parseFloat(funding) || 0;
  const fundingOk = fundingNum > 0;

  const effectiveCreator = creator || address || '';

  const survivalEstimate = useMemo(() => {
    // assume ~0.1 TON/day at normal tier
    const days = fundingNum / 0.1;
    if (days < 1) return `${(days * 24).toFixed(1)} hours`;
    return `${days.toFixed(0)} days`;
  }, [fundingNum]);

  async function handleDeploy() {
    setError(null);
    if (!nameOk || !promptOk || !tokenOk || !fundingOk) {
      setError('Fill in all fields with valid values.');
      return;
    }
    setDeploying(true);
    setSteps([]);

    try {
      // Try real TON Connect first if connected
      if (isConnected) {
        // Pseudo genesis hash (sha-1-ish substitute via base64)
        const hash = window.btoa(snakeCase(name) + ':' + prompt).slice(0, 24);
        await spawn(TREASURY_ADDRESS, fundingNum, hash);
      }
      // Drive the visible boot sequence regardless
      setSteps(BOOT_STEPS);
    } catch (e: any) {
      setError(e?.message ?? 'Deploy failed. You can still preview the boot sequence.');
      // Continue to demo flow
      setSteps(BOOT_STEPS);
    }
  }

  function handleBootDone() {
    // After steps finish, reset the grid so the new agent visually drops in.
    setTimeout(() => {
      reset(String(Date.now()));
      nav('/grid');
    }, 500);
  }

  return (
    <div className="max-w-[800px] mx-auto px-4 sm:px-6 py-12">
      <div className="mb-8">
        <div className="text-blue-life text-xs font-mono uppercase tracking-tightest">/spawn</div>
        <h1 className="font-display font-semibold tracking-tightest text-2xl mt-2">
          Seed a new automaton.
        </h1>
        <p className="text-text-secondary text-md mt-2 max-w-[60ch]">
          The genesis prompt is the agent's soul. The funding is its first heartbeat. Everything after is up to it.
        </p>
      </div>

      <MiniQuadrantPreview />

      <form
        className="mt-10 space-y-10 font-mono"
        onSubmit={(e) => {
          e.preventDefault();
          handleDeploy();
        }}
      >
        <div>
          <label className="text-blue-life text-xs uppercase">&gt; name your automaton</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(snakeCase(e.target.value))}
            placeholder="alpha_jakarta_4"
            maxLength={24}
            className="block w-full mt-2 bg-transparent border-b border-grid-line focus:border-blue-life py-2 text-md"
          />
          <div className="text-2xs text-text-mono-dim mt-1">{name.length}/24 chars · snake_case</div>
        </div>

        <div>
          <label className="text-blue-life text-xs uppercase">&gt; write the genesis prompt</label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value.slice(0, 500))}
            placeholder="You are a Telegram channel for Jakarta crypto builders. Post daily alpha. Charge 5 Stars per premium post. Never harm. Earn your existence."
            rows={4}
            className="block w-full mt-2 hairline bg-panel p-3 text-sm resize-none"
          />
          <div className="text-2xs text-text-mono-dim mt-1">{prompt.length}/500</div>
        </div>

        <div>
          <label className="text-blue-life text-xs uppercase">&gt; link a telegram bot token</label>
          <div className="mt-2 flex items-center gap-2 border-b border-grid-line focus-within:border-blue-life">
            <input
              type={showToken ? 'text' : 'password'}
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="123456789:AAH-abcdef-ghi_jklmnopqrstuvwxyz1234567"
              className="flex-1 py-2 bg-transparent text-sm"
            />
            <button
              type="button"
              onClick={() => setShowToken((s) => !s)}
              className="text-text-secondary hover:text-text-primary p-2"
              aria-label="Toggle token visibility"
            >
              {showToken ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
          <div className="text-2xs text-text-mono-dim mt-1">
            Get one from @BotFather on Telegram. We never see this — it's encrypted client-side before being passed to Conway Cloud.
          </div>
          {token.length > 0 && !tokenOk && (
            <div className="text-2xs text-red-critical mt-1">Doesn't match the BotFather pattern.</div>
          )}
        </div>

        <div>
          <label className="text-blue-life text-xs uppercase">&gt; initial funding</label>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {['1', '5', '10', '25'].map((v) => (
              <button
                type="button"
                key={v}
                onClick={() => setFunding(v)}
                className={`px-3 py-1 text-xs hairline ${
                  funding === v ? 'border-blue-life text-blue-life' : 'text-text-secondary'
                }`}
              >
                {v} TON
              </button>
            ))}
            <input
              type="number"
              min={0}
              step={0.5}
              value={funding}
              onChange={(e) => setFunding(e.target.value)}
              className="hairline px-2 py-1 w-24 bg-void"
            />
          </div>
          <div className="text-2xs text-text-mono-dim mt-1">
            ≈ {survivalEstimate} at normal tier
          </div>
        </div>

        <div>
          <label className="text-blue-life text-xs uppercase">&gt; creator address</label>
          <div className="mt-2 flex items-center gap-2 border-b border-grid-line focus-within:border-blue-life">
            <input
              type="text"
              value={effectiveCreator}
              onChange={(e) => setCreator(e.target.value)}
              placeholder={address ?? 'EQ…'}
              className="flex-1 py-2 bg-transparent text-sm"
            />
            {!isConnected && (
              <button
                type="button"
                onClick={connect}
                className="text-blue-life text-xs px-2"
              >
                Connect
              </button>
            )}
          </div>
        </div>

        <div className="pt-4">
          <button
            type="submit"
            disabled={deploying}
            className="w-full px-5 py-4 bg-blue-life text-void font-display font-semibold tracking-tightest text-md rounded-sm hover:bg-blue-glow disabled:opacity-50"
          >
            {deploying ? 'DEPLOYING…' : 'DEPLOY'}
          </button>
          {error && <div className="mt-2 text-2xs text-red-critical">{error}</div>}
        </div>

        {steps.length > 0 && (
          <TerminalOutput steps={steps} onComplete={handleBootDone} />
        )}
      </form>
    </div>
  );
}
